import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { shortDate } from '../format';
import type { LeaseDocument } from '../types';

const ROLE_LABELS: Record<string, string> = {
  BAIL: 'Bail',
  CHARTE: 'Charte de colocation',
  ATTESTATION: 'Attestation de virement',
  CAUTIONNEMENT: 'Acte de cautionnement',
  CERTIFICAT_SIGNATURE: 'Certificat de preuve',
  ETAT_DES_LIEUX: 'État des lieux',
  PIECE_ADMINISTRATIVE: 'Pièce justificative',
  DPE: 'DPE',
  NOTICE: "Notice d'information",
  REGLEMENT: 'Règlement de copropriété',
  AUTRE: 'Autre',
};

const UPLOADABLE = ['PIECE_ADMINISTRATIVE', 'BAIL', 'CHARTE', 'ATTESTATION', 'CAUTIONNEMENT', 'AUTRE'];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function LeaseDocuments({ leaseId }: { leaseId: string }) {
  const [documents, setDocuments] = useState<LeaseDocument[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState('PIECE_ADMINISTRATIVE');
  const [signed, setSigned] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function load(): Promise<void> {
    try {
      const result = await api<{ documents: LeaseDocument[] }>(`/api/admin/leases/${leaseId}/documents`);
      setDocuments(result.documents);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, [leaseId]);

  async function upload(file: File): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('role', role);
      form.append('signed', String(signed));
      form.append('file', file, file.name);
      await api(`/api/admin/leases/${leaseId}/documents`, { method: 'POST', body: form });
      if (fileInput.current) fileInput.current.value = '';
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="lease-documents">
      <div className="section-heading">
        <div><p className="eyebrow">Dossier</p><h3>Documents</h3></div>
        <a className="button compact" href={`/api/admin/leases/${leaseId}/archive`}>Télécharger le dossier</a>
      </div>
      <p className="section-intro">
        Tout se range ici : bail, charte, état des lieux et pièces justificatives. Le bouton ci-dessus
        produit une archive à l'arborescence prête à déposer dans votre Drive.
      </p>

      <div className="upload-row">
        <label>Type
          <select value={role} onChange={(event) => setRole(event.target.value)}>
            {UPLOADABLE.map((value) => <option key={value} value={value}>{ROLE_LABELS[value]}</option>)}
          </select>
        </label>
        <label className="check-field">
          <input type="checkbox" checked={signed} onChange={(event) => setSigned(event.target.checked)} />
          <span>Exemplaire signé</span>
        </label>
        <label>Fichier
          <input
            ref={fileInput}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.docx"
            disabled={busy}
            onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(file); }}
          />
        </label>
      </div>
      {busy && <p className="muted">Envoi en cours…</p>}
      {error && <div className="inline-message error" role="alert">{error}</div>}

      {documents?.length === 0 && <div className="empty-state">Aucun document pour ce bail.</div>}
      <div className="record-list">
        {documents?.map((document) => (
          <article className="record-card" key={document.id}>
            <div className="record-heading">
              <div>
                <strong>{ROLE_LABELS[document.role ?? 'AUTRE'] ?? document.role}</strong>
                <span className="record-code">{document.originalName}</span>
              </div>
              <span className={`status ${document.signed ? 'signed' : 'draft'}`}>
                {document.signed ? 'Signé' : 'Non signé'}
              </span>
            </div>
            <p className="record-line muted">
              <span>{formatSize(document.sizeBytes)} · ajouté le {shortDate(document.createdAt.slice(0, 10))}</span>
              <a href={document.url} target="_blank" rel="noreferrer">Ouvrir</a>
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
